import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { ENTITY_TYPES } from '@/constants/entityTypes';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATIC_BRAND_FIELDS = [
  'main_logo',
  'favicon',
  'hero_image',
  'about_image',
  'default_entity_image',
  'login_background',
  'dashboard_hero',
  'map_pin_default',
];

const buildEmptyBrandSettings = () => {
  const base = Object.fromEntries(STATIC_BRAND_FIELDS.map((k) => [k, '']));
  ENTITY_TYPES.forEach((t) => {
    base[`map_pin_${t.value}`] = '';
  });
  return base;
};

/**
 * Brand Settings tab — loads theme settings, uploads brand images,
 * saves the whole settings document back.
 *
 * Preserves any legacy `map_pin_*` keys still present in the DB so
 * super admins can migrate them manually if needed.
 */
export function useAdminBrand({ token, activeTab, isAdmin }) {
  const [themeSettings, setThemeSettings] = useState(buildEmptyBrandSettings);
  const [uploadingBrand, setUploadingBrand] = useState(false);

  const fetchThemeSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/theme-settings`);
      const next = Object.fromEntries(
        STATIC_BRAND_FIELDS.map((k) => [k, response.data[k] || ''])
      );
      ENTITY_TYPES.forEach((t) => {
        const k = `map_pin_${t.value}`;
        next[k] = response.data[k] || '';
      });
      // Preserve any legacy pin keys still in the DB (read-only carry-over)
      Object.keys(response.data || {}).forEach((k) => {
        if (k.startsWith('map_pin_') && !(k in next)) {
          next[k] = response.data[k] || '';
        }
      });
      setThemeSettings(next);
      toast.success('Theme settings loaded');
    } catch (error) {
      console.error('Error fetching theme settings:', error);
      toast.error('Failed to load theme settings');
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'brand' && isAdmin) {
      fetchThemeSettings();
    }
  }, [activeTab, isAdmin, fetchThemeSettings]);

  const handleBrandImageUpload = async (file, field) => {
    if (!file) return;
    setUploadingBrand(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const fileUrl = process.env.REACT_APP_BACKEND_URL + response.data.url;
      setThemeSettings((prev) => ({ ...prev, [field]: fileUrl }));
      toast.success(`${field.replace(/_/g, ' ')} uploaded successfully`);
    } catch (error) {
      console.error('Brand image upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingBrand(false);
    }
  };

  const handleSaveThemeSettings = async () => {
    try {
      await axios.put(`${API}/admin/theme-settings`, themeSettings, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      toast.success('Brand settings saved successfully');
    } catch (error) {
      console.error('Error saving theme settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save brand settings');
    }
  };

  return {
    themeSettings,
    uploadingBrand,
    fetchThemeSettings,
    handleBrandImageUpload,
    handleSaveThemeSettings,
  };
}
